import chroma from 'chroma-js';

/**
 * Curated explore palettes, generated deterministically so the grid is stable
 * across reloads and shareable. These are original, algorithmically-built
 * palettes grouped by mood/category (inspired by category-browsing color sites
 * like Color Hunt) — not copied from any external dataset.
 */

const PER_CATEGORY = 18;

function mulberry32(a) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

const clamp01 = v => Math.max(0, Math.min(1, v));
const wrapHue = h => ((h % 360) + 360) % 360;
const pick = (arr, rng) => arr[Math.floor(rng() * arr.length)];
const randIn = ([a, b], rng) => a + rng() * (b - a);

// id, label, candidate hue ranges, saturation [min,max], lightness [min,max], strategy.
export const CATEGORY_DEFS = [
  { id: 'pastel', label: 'Pastel', hues: [[0, 360]], sat: [0.28, 0.5], light: [0.74, 0.9], strat: 'analogous' },
  { id: 'vintage', label: 'Vintage', hues: [[15, 55], [180, 210]], sat: [0.22, 0.45], light: [0.35, 0.72], strat: 'analogous' },
  { id: 'retro', label: 'Retro', hues: [[10, 45], [170, 200]], sat: [0.45, 0.7], light: [0.4, 0.7], strat: 'fixedish' },
  { id: 'neon', label: 'Neon', hues: [[0, 360]], sat: [0.85, 1], light: [0.5, 0.62], strat: 'spread' },
  { id: 'gold', label: 'Gold', hues: [[38, 50]], sat: [0.5, 0.85], light: [0.35, 0.8], strat: 'ramp' },
  { id: 'light', label: 'Light', hues: [[0, 360]], sat: [0.12, 0.35], light: [0.85, 0.97], strat: 'analogous' },
  { id: 'dark', label: 'Dark', hues: [[200, 260]], sat: [0.3, 0.6], light: [0.12, 0.35], strat: 'ramp' },
  { id: 'warm', label: 'Warm', hues: [[0, 55], [330, 360]], sat: [0.5, 0.8], light: [0.45, 0.7], strat: 'analogous' },
  { id: 'cold', label: 'Cold', hues: [[185, 260]], sat: [0.4, 0.7], light: [0.4, 0.75], strat: 'analogous' },
  { id: 'summer', label: 'Summer', hues: [[20, 55], [170, 200]], sat: [0.6, 0.9], light: [0.5, 0.7], strat: 'fixedish' },
  { id: 'fall', label: 'Fall', hues: [[20, 45]], sat: [0.4, 0.75], light: [0.3, 0.65], strat: 'ramp' },
  { id: 'winter', label: 'Winter', hues: [[195, 235]], sat: [0.2, 0.5], light: [0.45, 0.85], strat: 'ramp' },
  { id: 'spring', label: 'Spring', hues: [[80, 160], [330, 350]], sat: [0.4, 0.6], light: [0.65, 0.85], strat: 'analogous' },
  { id: 'happy', label: 'Happy', hues: [[0, 360]], sat: [0.7, 0.95], light: [0.55, 0.68], strat: 'spread' },
  { id: 'nature', label: 'Nature', hues: [[80, 150]], sat: [0.35, 0.6], light: [0.3, 0.65], strat: 'ramp' },
  { id: 'earth', label: 'Earth', hues: [[25, 55]], sat: [0.3, 0.55], light: [0.35, 0.7], strat: 'ramp' },
  { id: 'night', label: 'Night', hues: [[220, 270]], sat: [0.35, 0.6], light: [0.15, 0.4], strat: 'ramp' },
  { id: 'space', label: 'Space', hues: [[230, 280]], sat: [0.4, 0.7], light: [0.1, 0.35], strat: 'ramp' },
  { id: 'rainbow', label: 'Rainbow', hues: [[0, 360]], sat: [0.6, 0.85], light: [0.5, 0.62], strat: 'spread' },
  { id: 'gradient', label: 'Gradient', hues: [[0, 360]], sat: [0.55, 0.8], light: [0.3, 0.8], strat: 'ramp' },
  { id: 'sunset', label: 'Sunset', hues: [[0, 40], [300, 330]], sat: [0.6, 0.85], light: [0.5, 0.7], strat: 'fixedish' },
  { id: 'sky', label: 'Sky', hues: [[195, 225]], sat: [0.3, 0.6], light: [0.6, 0.9], strat: 'ramp' },
  { id: 'sea', label: 'Sea', hues: [[160, 210]], sat: [0.45, 0.75], light: [0.35, 0.7], strat: 'ramp' },
  { id: 'kids', label: 'Kids', hues: [[0, 360]], sat: [0.7, 0.9], light: [0.58, 0.68], strat: 'spread' },
  { id: 'skin', label: 'Skin', hues: [[18, 35]], sat: [0.3, 0.55], light: [0.5, 0.85], strat: 'ramp' },
  { id: 'food', label: 'Food', hues: [[5, 45]], sat: [0.55, 0.8], light: [0.45, 0.65], strat: 'analogous' },
  { id: 'cream', label: 'Cream', hues: [[35, 50]], sat: [0.2, 0.4], light: [0.8, 0.95], strat: 'ramp' },
  { id: 'coffee', label: 'Coffee', hues: [[20, 35]], sat: [0.3, 0.55], light: [0.25, 0.6], strat: 'ramp' },
  { id: 'wedding', label: 'Wedding', hues: [[340, 360], [35, 45]], sat: [0.15, 0.4], light: [0.78, 0.93], strat: 'analogous' },
  { id: 'christmas', label: 'Christmas', hues: [[0, 10], [140, 160], [40, 50]], sat: [0.5, 0.75], light: [0.3, 0.6], strat: 'fixedish' },
  { id: 'halloween', label: 'Halloween', hues: [[20, 35], [270, 290]], sat: [0.6, 0.85], light: [0.35, 0.6], strat: 'fixedish' },
];

export const CATEGORIES = CATEGORY_DEFS.map(({ id, label }) => ({ id, label }));

function buildColors(cfg, rng) {
  const { sat, light, hues, strat } = cfg;
  const hex = (h, s, l) => chroma.hsl(wrapHue(h), clamp01(s), clamp01(l)).hex();

  if (strat === 'spread') {
    const start = rng() * 360;
    return [0, 1, 2, 3].map(i =>
      hex(start + i * (90 + (rng() * 30 - 15)), randIn(sat, rng), randIn(light, rng))
    );
  }
  if (strat === 'fixedish') {
    return [0, 1, 2, 3].map(i => {
      const range = hues[i % hues.length];
      return hex(randIn(range, rng) + (rng() * 10 - 5), randIn(sat, rng), randIn(light, rng));
    });
  }
  if (strat === 'analogous') {
    const base = randIn(pick(hues, rng), rng);
    return [-26, -9, 9, 26].map(off =>
      hex(base + off + (rng() * 8 - 4), randIn(sat, rng), randIn(light, rng))
    );
  }
  // 'ramp': single base hue, lightness gradient (light -> dark).
  const base = randIn(pick(hues, rng), rng);
  const [lo, hi] = light;
  return [0, 1, 2, 3].map(i => {
    const l = hi - (hi - lo) * (i / 3) + (rng() * 0.05 - 0.025);
    return hex(base + (rng() * 16 - 8), randIn(sat, rng), l);
  });
}

let _all = null;

/**
 * @returns {Array<{id,category,colors:string[],likes:number,createdAt:number}>}
 */
export function getAllPalettes() {
  if (_all) return _all;
  const out = [];
  let index = 0;
  CATEGORY_DEFS.forEach(cfg => {
    for (let i = 0; i < PER_CATEGORY; i++) {
      const seed = (hashString(cfg.id) + i * 2654435761) >>> 0;
      const rng = mulberry32(seed);
      const colors = buildColors(cfg, rng);
      const likeRng = mulberry32((seed ^ 0x9e3779b9) >>> 0);
      out.push({
        id: `${cfg.id}-${i}`,
        category: cfg.id,
        colors,
        likes: Math.floor(likeRng() * 1990) + 30,
        createdAt: index++,
      });
    }
  });
  _all = out;
  return out;
}
